import { useEffect } from 'react';

/**
 * Category Analytics and Tracking System
 * Provides comprehensive analytics for category performance and user behavior
 */

class CategoryAnalytics {
  constructor() {
    this.events = [];
    this.sessionData = {
      sessionId: this.generateSessionId(),
      startTime: Date.now(),
      pageViews: [],
      interactions: [],
      conversionFunnel: []
    };
    
    // Initialize tracking
    this.initializeTracking();
  }

  generateSessionId() {
    return 'cat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  initializeTracking() {
    // Track page visibility
    document.addEventListener('visibilitychange', () => {
      this.trackEvent('page_visibility', {
        hidden: document.hidden,
        timestamp: Date.now()
      });
    });

    // Track scroll depth
    let maxScrollDepth = 0;
    window.addEventListener('scroll', () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollPercent > maxScrollDepth) {
        maxScrollDepth = scrollPercent;
        this.trackEvent('scroll_depth', {
          depth: scrollPercent,
          timestamp: Date.now()
        });
      }
    });
  }

  // Core tracking methods
  trackEvent(eventName, data = {}) {
    const event = {
      eventName,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      url: window.location.pathname,
      userAgent: navigator.userAgent,
      ...data
    };

    this.events.push(event);
    this.sessionData.interactions.push(event);

    // Send to analytics service (mock implementation)
    if (process.env.NODE_ENV === 'production') {
      this.sendToAnalytics(event);
    } else {
      console.log('📊 Analytics Event:', event);
    }
  }

  // Category-specific tracking methods
  trackCategoryView(categoryType, categoryConfig) {
    const pageView = {
      categoryType,
      categoryName: categoryConfig.name,
      displayName: categoryConfig.displayName,
      timestamp: Date.now(),
      referrer: document.referrer
    };

    this.sessionData.pageViews.push(pageView);
    
    this.trackEvent('category_view', {
      category: categoryType,
      categoryName: categoryConfig.name,
      popularity: categoryConfig.getMetadata('popularityScore'),
      features: categoryConfig.features
    });
  }

  trackServiceInteraction(action, service, categoryType) {
    this.trackEvent('service_interaction', {
      action, // 'view', 'learn_more', 'book_now', 'favorite'
      serviceId: service.id,
      serviceTitle: service.title,
      servicePrice: service.price,
      categoryType,
      rating: service.rating
    });

    // Track conversion funnel
    if (action === 'book_now') {
      this.sessionData.conversionFunnel.push({
        step: 'booking_initiated',
        serviceId: service.id,
        categoryType,
        timestamp: Date.now()
      });
    }
  }

  trackSearch(query, categoryType, resultsCount) {
    this.trackEvent('category_search', {
      query,
      categoryType,
      resultsCount,
      queryLength: query.length
    });
  }

  trackFilter(filterType, filterValue, categoryType, resultsCount) {
    this.trackEvent('category_filter', {
      filterType,
      filterValue,
      categoryType,
      resultsCount
    });
  }

  trackError(error, context = {}) {
    this.trackEvent('category_error', {
      error: error.message,
      stack: error.stack,
      context
    });
  }

  // Performance tracking
  trackPerformance(componentName, renderTime, categoryType) {
    this.trackEvent('performance_metric', {
      componentName,
      renderTime,
      categoryType,
      timestamp: Date.now()
    });
  }

  trackLoadTime(categoryType, loadTime) {
    this.trackEvent('category_load_time', {
      categoryType,
      loadTime,
      timestamp: Date.now()
    });
  }

  // User behavior analysis
  getSessionSummary() {
    const sessionDuration = Date.now() - this.sessionData.startTime;
    const uniqueCategories = [...new Set(this.sessionData.pageViews.map(pv => pv.categoryType))];
    const totalInteractions = this.sessionData.interactions.length;
    
    return {
      sessionId: this.sessionData.sessionId,
      duration: sessionDuration,
      pageViews: this.sessionData.pageViews.length,
      uniqueCategories: uniqueCategories.length,
      categoriesVisited: uniqueCategories,
      totalInteractions,
      conversionEvents: this.sessionData.conversionFunnel.length,
      averageTimePerPage: sessionDuration / Math.max(this.sessionData.pageViews.length, 1)
    };
  }

  getCategoryMetrics(categoryType) {
    const categoryEvents = this.events.filter(e => e.categoryType === categoryType);
    const views = categoryEvents.filter(e => e.eventName === 'category_view').length;
    const interactions = categoryEvents.filter(e => e.eventName === 'service_interaction').length;
    const searches = categoryEvents.filter(e => e.eventName === 'category_search').length;
    
    return {
      categoryType,
      views,
      interactions,
      searches,
      conversionRate: interactions > 0 ? (interactions / views) * 100 : 0,
      engagementScore: (interactions + searches) / Math.max(views, 1)
    };
  }

  // A/B testing support
  trackABTest(testName, variant, categoryType) {
    this.trackEvent('ab_test', {
      testName,
      variant,
      categoryType
    });
  }

  // Heatmap data collection
  trackClickPosition(x, y, element, categoryType) {
    this.trackEvent('click_heatmap', {
      x,
      y,
      element: element.tagName,
      elementClass: element.className,
      categoryType
    });
  }

  // Send data to analytics service
  async sendToAnalytics(event) {
    try {
      // Mock API call - replace with actual analytics service
      await fetch('/api/analytics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(event)
      });
    } catch (error) {
      console.warn('Failed to send analytics event:', error);
    }
  }

  // Export data for analysis
  exportData() {
    return {
      events: this.events,
      sessionData: this.sessionData,
      summary: this.getSessionSummary()
    };
  }

  // Clear data (for privacy compliance)
  clearData() {
    this.events = [];
    this.sessionData = {
      sessionId: this.generateSessionId(),
      startTime: Date.now(),
      pageViews: [],
      interactions: [],
      conversionFunnel: []
    };
  }
}

// Singleton instance
const categoryAnalytics = new CategoryAnalytics();

// React hooks for analytics
export const useCategoryAnalytics = () => {
  return {
    trackCategoryView: (categoryType, categoryConfig) => 
      categoryAnalytics.trackCategoryView(categoryType, categoryConfig),
    
    trackServiceInteraction: (action, service, categoryType) =>
      categoryAnalytics.trackServiceInteraction(action, service, categoryType),
    
    trackSearch: (query, categoryType, resultsCount) =>
      categoryAnalytics.trackSearch(query, categoryType, resultsCount),
    
    trackFilter: (filterType, filterValue, categoryType, resultsCount) =>
      categoryAnalytics.trackFilter(filterType, filterValue, categoryType, resultsCount),
    
    trackError: (error, context) =>
      categoryAnalytics.trackError(error, context),
    
    trackPerformance: (componentName, renderTime, categoryType) =>
      categoryAnalytics.trackPerformance(componentName, renderTime, categoryType),
    
    trackABTest: (testName, variant, categoryType) =>
      categoryAnalytics.trackABTest(testName, variant, categoryType),
    
    getSessionSummary: () => categoryAnalytics.getSessionSummary(),
    getCategoryMetrics: (categoryType) => categoryAnalytics.getCategoryMetrics(categoryType)
  };
};

// HOC for automatic analytics tracking
export const withCategoryAnalytics = (WrappedComponent, componentName) => {
  return function AnalyticsWrappedComponent(props) {
    const analytics = useCategoryAnalytics();
    
    useEffect(() => {
      const startTime = performance.now();
      
      return () => {
        const endTime = performance.now();
        const renderTime = endTime - startTime;
        analytics.trackPerformance(componentName, renderTime, props.categoryType);
      };
    }, [analytics, props.categoryType]);

    return <WrappedComponent {...props} analytics={analytics} />;
  };
};

export default categoryAnalytics;
